// In-memory URL state — a UrlStateLike that holds query params in a plain object
// instead of window.location.search. Used by the in-editor file-picker modal so
// its filter/sort/page state never touches the browser hash/route (which would
// corrupt the editor's own route + back-button while the user is mid-edit).
//
// Mirrors createUrlState's surface and canonicalization exactly (sort keys, strip
// empty, sort `unorderedKeys` arrays) so consumers — filter-bar, data-grid, and
// createGatedUrlState (which pass-throughs `replace`) — behave identically whether
// given the real hash url-state or this one. The only difference is the backing
// store: a module-private object, never the global location.

/**
 * @typedef {import('./url-state.js').UrlStateListener} UrlStateListener
 * @typedef {import('./gated-url-state.js').UrlStateLike} UrlStateLike
 */

/**
 * Creates an in-memory URL state manager (no window.location coupling).
 *
 * @param {import('./url-state.js').UrlStateOptions} [options]
 * @returns {UrlStateLike}
 */
export function createInMemoryUrlState(options) {
  const unorderedKeys = options?.unorderedKeys ?? new Set();

  /** @type {Set<UrlStateListener>} */
  const listeners = new Set();

  /**
   * Canonical state string + parsed object. We keep the params as a canonical
   * record (sorted keys, empties stripped, unordered arrays sorted) so the
   * no-op detection in set/setMany/replace matches the hash url-state.
   * @type {Record<string, string | string[]>}
   */
  let state = {};

  /**
   * Canonicalize an object: drop empties, sort `unorderedKeys` array values,
   * and (for stable comparison) reduce single-element arrays to strings — same
   * normalization the hash url-state produces when reading back from the URL.
   * @param {Record<string, string | string[]>} obj
   * @returns {Record<string, string | string[]>}
   */
  function canonicalize(obj) {
    /** @type {Record<string, string | string[]>} */
    const result = {};
    for (const key of Object.keys(obj).sort()) {
      const value = obj[key];
      if (isEmpty(value)) continue;
      if (Array.isArray(value)) {
        const items = [...value].filter(v => v !== '');
        if (items.length === 0) continue;
        if (unorderedKeys.has(key)) items.sort();
        result[key] = items.length === 1 ? items[0] : items;
      } else {
        result[key] = value;
      }
    }
    return result;
  }

  /**
   * @param {string | string[]} value
   * @returns {boolean}
   */
  function isEmpty(value) {
    return value === '' || (Array.isArray(value) && value.length === 0);
  }

  /**
   * Stable string form of the canonical state for no-op comparison. Keys are
   * already sorted by canonicalize. Uses JSON so distinct shapes can't collide
   * (e.g. the array ['7','3'] vs the string '73', or ['a','b'] vs ['ab']).
   * @param {Record<string, string | string[]>} obj
   * @returns {string}
   */
  function serialize(obj) {
    return JSON.stringify(obj);
  }

  /**
   * Snapshot the current state (fresh object; callers can't mutate internals).
   * @returns {Record<string, string | string[]>}
   */
  function toQueryObject() {
    /** @type {Record<string, string | string[]>} */
    const copy = {};
    for (const [k, v] of Object.entries(state)) {
      copy[k] = Array.isArray(v) ? [...v] : v;
    }
    return copy;
  }

  /**
   * @param {string} key
   * @returns {string | string[] | undefined}
   */
  function get(key) {
    const v = state[key];
    if (v === undefined) return undefined;
    return Array.isArray(v) ? [...v] : v;
  }

  /**
   * @param {Record<string, string | string[]>} next
   */
  function commit(next) {
    const canonical = canonicalize(next);
    if (serialize(canonical) === serialize(state)) return; // no-op
    state = canonical;
    notify();
  }

  /**
   * @param {string} key
   * @param {string | string[]} value
   */
  function set(key, value) {
    const current = toQueryObject();
    if (isEmpty(value)) delete current[key];
    else current[key] = value;
    commit(current);
  }

  /**
   * @param {Record<string, string | string[]>} updates
   */
  function setMany(updates) {
    const current = toQueryObject();
    for (const [key, value] of Object.entries(updates)) {
      if (isEmpty(value)) delete current[key];
      else current[key] = value;
    }
    commit(current);
  }

  /**
   * @param {Record<string, string | string[]>} params
   */
  function replace(params) {
    commit({ ...params });
  }

  function notify() {
    const current = toQueryObject();
    for (const listener of [...listeners]) {
      listener(current);
    }
  }

  /**
   * @param {UrlStateListener} listener
   * @returns {() => void}
   */
  function subscribe(listener) {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }

  return { get, set, setMany, replace, subscribe, toQueryObject };
}
